import { Share, DotsThree } from '@phosphor-icons/react';
import { Menu } from '@headlessui/react';
import { useState } from 'react';
import { Modal } from '../../../shared/components/layout/Modal';
import { useChatStore } from '../../../shared/store/chatStore';
import { useNavigate, Link } from 'react-router-dom';
import { Logo } from '../../../shared/components/layout/Logo';
import { chatApi } from '../../../shared/api/chatApi';
import { toast } from 'react-hot-toast';

interface ChatHeaderProps {
    onShare: () => void;
    chatId: string;
}

const REPORT_REASONS = [
    'Inappropriate content',
    'Spam',
    'Harassment',
    'Hate speech',
    'Violence',
    'Other'
] as const;

type ReportReason = typeof REPORT_REASONS[number];

export function ChatHeader({ onShare, chatId }: ChatHeaderProps) {
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const deleteChat = useChatStore(state => state.deleteChat);
    const navigate = useNavigate();

    const handleDelete = async () => {
        try {
            await deleteChat(chatId);
            navigate('/');
        } catch (error) {
            console.error('Error deleting chat:', error);
        }
    };

    const handleReport = async () => {
        if (!selectedReason) return;

        setIsSubmitting(true);
        try {
            await chatApi.reportChat(chatId, { reason: selectedReason });
            toast.success('Chat reported successfully');
            setIsReportModalOpen(false);
            setSelectedReason(null);
        } catch (error) {
            console.error('Error reporting chat:', error);
            toast.error('Failed to report chat');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed top-0 left-0 sm:left-16 right-0 h-16 z-10">
            <div className="h-full w-full flex items-center justify-between px-4">
                <Link to="/" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <Logo size={32} className="text-[#361E06]" />
                </Link>
                
                <div className="flex items-center gap-2 ml-auto">
                    <button
                        onClick={onShare}
                        className="inline-flex items-center px-3 py-2 bg-[rgba(0,0,0,0.05)] hover:bg-[rgba(0,0,0,0.1)] color-[#361E06] text-sm font-medium rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <Share className="w-4 h-4 mr-2" weight="bold" />
                        Share chat
                    </button>

                    <Menu as="div" className="relative">
                        <Menu.Button className="inline-flex items-center px-2 py-2 bg-[rgba(0,0,0,0.05)] hover:bg-[rgba(0,0,0,0.1)] text-sm font-medium rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            <DotsThree className="w-5 h-5 text-[#361E06]" weight="bold" />
                        </Menu.Button>

                        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="py-1">
                                <Menu.Item>
                                    {({ active }) => (
                                        <button
                                            onClick={handleDelete}
                                            className={`${
                                                active ? 'bg-gray-100' : ''
                                            } block w-full text-left px-4 py-2 text-sm text-red-600 hover:text-red-700`}
                                        >
                                            Delete chat
                                        </button>
                                    )}
                                </Menu.Item>
                                <Menu.Item>
                                    {({ active }) => (
                                        <button
                                            onClick={() => setIsReportModalOpen(true)}
                                            className={`${
                                                active ? 'bg-gray-100' : ''
                                            } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                                        >
                                            Report chat
                                        </button>
                                    )}
                                </Menu.Item>
                            </div>
                        </Menu.Items>
                    </Menu>
                </div>
            </div>

            <Modal
                isOpen={isReportModalOpen}
                onClose={() => {
                    setIsReportModalOpen(false);
                    setSelectedReason(null);
                }}
                title="Report Chat"
                variant="dialog"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-500">
                        Please select a reason for reporting this chat:
                    </p>

                    <div className="space-y-2">
                        {REPORT_REASONS.map((reason) => (
                            <button
                                key={reason}
                                onClick={() => setSelectedReason(reason)}
                                className={`w-full text-left px-4 py-3 rounded-lg flex items-center justify-between ${
                                    selectedReason === reason
                                        ? 'bg-[#361E06] text-white'
                                        : 'hover:bg-gray-50 text-gray-700'
                                }`}
                            >
                                {reason}
                            </button>
                        ))}
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={() => {
                                setIsReportModalOpen(false);
                                setSelectedReason(null);
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#361E06]"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleReport}
                            disabled={!selectedReason || isSubmitting}
                            className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                                selectedReason && !isSubmitting
                                    ? 'bg-red-600 hover:bg-red-700'
                                    : 'bg-gray-300 cursor-not-allowed'
                            }`}
                        >
                            {isSubmitting ? 'Reporting...' : 'Report'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
